import { cn } from '@sjuni/ui';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import { visibleNavigation } from '@/lib/nav/navigation';
import { useAuth } from '@/lib/auth/useAuth';

import { NavIcon } from './NavIcon';

/**
 * docs/05-ADMIN-DASHBOARD.md §1 — the collapsible sidebar and its groups.
 *
 * Which entries appear is `visibleNavigation`'s decision, and it hides a whole
 * group when nothing inside it is reachable: a Document Manager staring at an
 * empty CONTENT heading learns only that something is missing.
 *
 * Collapsed it keeps the icons and drops the labels, so the muscle memory
 * survives. The labels stay in the accessibility tree via `title` and the
 * accessible name, because an icon-only rail is otherwise a row of unlabelled
 * links.
 */
export interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  /** Mobile only: the drawer closes when a link is followed. */
  onNavigate?: () => void;
  className?: string;
}

export function Sidebar({ collapsed, onToggle, onNavigate, className }: SidebarProps) {
  const { t } = useTranslation();
  const { can, hasRole } = useAuth();

  const groups = visibleNavigation({ can, isSuperAdmin: hasRole('super-admin') });

  return (
    <nav
      aria-label={t('nav.label')}
      className={cn(
        'flex h-full flex-col border-e border-subtle bg-surface',
        collapsed ? 'w-16' : 'w-64',
        className,
      )}
    >
      <div className="flex-1 overflow-y-auto px-3 py-4">
        {groups.map((group) => (
          <div key={group.key} className="mb-6 last:mb-0">
            {/*
              The dashboard is one item and needs no heading over it. Every
              other group gets one — and it is a real heading, so a screen
              reader can jump between them rather than walking thirty links.
            */}
            {group.key === 'dashboard' ? null : (
              <h2
                className={cn(
                  'mb-2 px-3 text-caption font-extrabold tracking-wide text-muted',
                  // Hidden visually when collapsed, never removed: the groups
                  // are how the list is navigable at all.
                  collapsed && 'sr-only',
                )}
              >
                {t(`nav.groups.${group.key}`)}
              </h2>
            )}

            <ul className="space-y-1">
              {group.items.map((item) => (
                <li key={item.key}>
                  <NavLink
                    to={item.path}
                    // `end` only for the dashboard: without it `/` matches
                    // every path and the whole sidebar reads as current.
                    end={item.path === '/'}
                    onClick={onNavigate}
                    title={collapsed ? t(`nav.items.${item.key}`) : undefined}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 rounded-card px-3 py-2 text-body-sm transition-colors duration-fast',
                        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600',
                        isActive
                          ? 'bg-accent-tint-navy font-extrabold text-primary'
                          : 'text-secondary hover:bg-surface-alt hover:text-primary',
                        collapsed && 'justify-center px-0',
                      )
                    }
                  >
                    <NavIcon name={item.icon} className="shrink-0" />
                    <span className={cn(collapsed && 'sr-only')}>{t(`nav.items.${item.key}`)}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="border-t border-subtle p-3">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={!collapsed}
          className="flex w-full items-center gap-3 rounded-card px-3 py-2 text-body-sm text-secondary transition-colors duration-fast hover:bg-surface-alt hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
        >
          {collapsed ? (
            <PanelLeftOpen size={18} strokeWidth={1.75} aria-hidden="true" className="mx-auto" />
          ) : (
            <>
              <PanelLeftClose size={18} strokeWidth={1.75} aria-hidden="true" />
              {t('nav.collapse')}
            </>
          )}
          <span className={cn(collapsed && 'sr-only')}>{collapsed ? t('nav.expand') : null}</span>
        </button>
      </div>
    </nav>
  );
}
